import { Plus, ShieldAlert, Users, Wrench } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaInput } from "@/shared/components/forms/ColocaInput";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import { useStepUpRefusal } from "@/shared/hooks/useStepUpRefusal";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { hasStepUpMethod } from "@/shared/utils/stepUp";
import { colocaToast } from "@/shared/utils/toast";
import { isTwoFactorCodeError, stepUpRefusal } from "@/shared/utils/twoFactorErrors";

import { CARD_ALIAS_MAX_LENGTH } from "../constants/cards.constants";
import { resolveIdentityDisplayName } from "../constants/identityTiers.constants";
import { useCardOperationsMaintenance } from "../hooks/useCardOperationsMaintenance";
import { useCreateCardPockets } from "../hooks/useCreateCardPockets";
import { useIdentitiesList } from "../hooks/useIdentitiesList";
import { usePocketsVisibility } from "../hooks/usePocketsVisibility";
import { CardsService } from "../services/cardsService";
import { IdentitiesService } from "../services/identitiesService";
import type { Identity } from "../types/identities-api.types";
import { isEligibleHolder } from "../utils/holderEligibility";
import type { NewHolderOutcome } from "../utils/newHolderOutcome";
import { CardPreviewPanel } from "./create-card/CardPreviewPanel";
import { CardTypeSelector } from "./create-card/CardTypeSelector";
import { HolderPickerField } from "./create-card/HolderPickerField";
import { NewHolderDrawer } from "./create-card/NewHolderDrawer";
import { PocketPickerField } from "./create-card/PocketPickerField";
import { TwoFactorChallengeModal } from "./TwoFactorChallengeModal";

type CreateCardModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  presetHolderId?: string | null;
  presetPocketId?: string | null;
  /** When true, the pocket is fixed to `presetPocketId` and the pocket picker is hidden. */
  lockPocket?: boolean;
  onCreated?: (cardId: string) => void;
  onRefresh?: () => void | Promise<void>;
};

export const CreateCardModal = ({
  open,
  onOpenChange,
  presetHolderId = null,
  presetPocketId = null,
  lockPocket = false,
  onCreated,
  onRefresh,
}: CreateCardModalProps) => {
  const { t } = useTranslations("cards");
  const { user, can } = useAuth();
  const refusalMessage = useStepUpRefusal();
  const navigate = useNavigate();
  const { visible: pocketsVisible } = usePocketsVisibility();

  const [alias, setAlias] = useState("");
  const [isTwoFactorChallengeOpen, setIsTwoFactorChallengeOpen] = useState(false);
  const [holderId, setHolderId] = useState<string | null>(presetHolderId);
  const [pocketId, setPocketId] = useState<string | null>(presetPocketId);
  /**
   * A holder that is NOT on the loaded page — resolved by id. Two sources feed it: the `presetHolderId` a
   * caller deep-links with, and a holder just created from the drawer. Both need the same treatment, which is
   * why this is no longer named after the preset.
   */
  const [externalHolder, setExternalHolder] = useState<Identity | null>(null);
  const [pocketQuery, setPocketQuery] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isNewHolderOpen, setIsNewHolderOpen] = useState(false);
  /**
   * A holder created here that cannot be issued a card yet. Not a selection — an explanation. `id` is null
   * when the creation could not even be identified, which still has to be said out loud.
   */
  const [pendingHolder, setPendingHolder] = useState<{ id: string | null; name: string | null } | null>(null);

  // Creating a holder is `identities:write`, a different permission from the `cards:write` that got the
  // operator into this modal. Without it the affordance is absent rather than disabled: a dead button with
  // no stated reason is worse than no button.
  const canCreateHolder = can("identities:write");

  const { pockets, isLoading: pocketsLoading } = useCreateCardPockets(open && pocketsVisible);

  /*
    The holder list is UNFILTERED on purpose — the same request `/app/cards/identities` makes, with the search
    term applied server-side.

    It used to pin `status=ACTIVE` + `kyc_group=approved`. That hid every holder who was not yet issuable,
    which reads identically to "this person does not exist": the operator searched a name they had just
    created, found nothing, and created the person a second time. Now those holders are listed disabled with
    the badge that says what is blocking them, so the search answers the question that was actually asked.

    The backend never filtered any of this — `kyc_group` is an optional query param — so dropping the two
    fixed values needed no API change. What still enforces the rule is `POST /internal/cards`, which rejects a
    non-ACTIVE or non-VERIFIED holder with a 400; the disabled row is the affordance, not the boundary.
  */
  const {
    identities: holderPage,
    isLoading: holdersLoading,
    hasLoaded: holdersChecked,
    hasMore: hasMoreHolders,
    isLoadingMore: isLoadingMoreHolders,
    loadMore: loadMoreHolders,
    query: holderQuery,
    setQuery: setHolderQuery,
    isSearchTermTooShort: isHolderSearchTooShort,
    reload: resetHolderSearch,
  } = useIdentitiesList({
    loadErrorFallback: t("identities_error_load"),
    enabled: open,
    withAggregates: true,
  });

  useEffect(() => {
    if (!open) {
      setAlias("");
      setHolderId(presetHolderId);
      setPocketId(presetPocketId);
      setExternalHolder(null);
      setPendingHolder(null);
      // Without this the panel survives a close. `handleViewHolder` closes the modal WHILE the drawer is
      // open, and `identities/:id` re-renders rather than remounts, so the next "Crear tarjeta" on that page
      // opened straight into the drawer over a blank form.
      setIsNewHolderOpen(false);
      setPocketQuery("");
      // Clears the holder search so reopening does not resume the last term.
      // No request fires while `enabled` is false.
      resetHolderSearch();
    }
  }, [open, presetHolderId, presetPocketId, resetHolderSearch]);

  useEffect(() => {
    if (open && presetHolderId) setHolderId(presetHolderId);
  }, [open, presetHolderId]);

  useEffect(() => {
    if (open && presetPocketId) setPocketId(presetPocketId);
  }, [open, presetPocketId]);

  /**
   * A selected holder is not necessarily on the loaded page — a `presetHolderId` deep link may point past it,
   * and the page is smaller than the old 100-row fetch — so resolve it by id instead of hoping it is in the
   * list. Without this the picker would render as if nothing were selected while `holderId` was in fact set.
   *
   * Every path goes through `isEligibleHolder`, INCLUDING the one that finds the holder on the page. That
   * matters more now that the page carries ineligible rows: a `presetHolderId` aimed at one of them would
   * otherwise short-circuit the lookup and render it as a confidently selected holder, arming a submit the
   * backend rejects. Clicking is already impossible — those rows are disabled — but a deep link never asked
   * the picker's permission.
   *
   * Reaching here with an ineligible identity is routine, not a corner case: every identity is created
   * `PENDING_REVIEW` and only promoted to `ACTIVE` when card-provider registration succeeds, so a holder who
   * finished KYC but whose registration failed is `VERIFIED` + `PENDING_REVIEW`.
   *
   * An ineligible holder clears `holderId`, so the submit button disables itself and the picker honestly
   * reads "nothing selected" — with the holder still visible in the list, badged, which is the difference
   * from before. A failed lookup deliberately does NOT clear it: an ELIGIBLE holder outside the first page
   * whose request hit a network blip must not be turned into a dead end with no error and no retry.
   *
   * A holder just created in the drawer never reaches the request: `setExternalHolder` and `setHolderId` are
   * batched together, so the guard below already matches and the drawer's own read-back stays the only one.
   */
  useEffect(() => {
    if (!open || !holderId) return;
    const onPage = holderPage.find((holder) => holder.id === holderId);
    if (onPage) {
      if (!isEligibleHolder(onPage)) setHolderId(null);
      return;
    }
    if (externalHolder?.id === holderId) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await IdentitiesService.getIdentityById(holderId, { withAggregates: true });
        if (cancelled) return;
        // A body describing another identity must not be trusted, and must not be stored either: seeding
        // `externalHolder` with a mismatched id would leave the guard above unsatisfiable and re-fire this
        // effect forever. Same rule `resolveNewHolderOutcome` applies to its own read-back.
        if (response.data.id !== holderId) return;
        if (isEligibleHolder(response.data)) {
          setExternalHolder(response.data);
          return;
        }
        setHolderId(null);
      } catch {
        // Leaves the picker with no selection rendered; submitting still
        // carries the id, so a transient failure does not block the flow.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, holderId, holderPage, externalHolder]);

  /**
   * Same eligibility guard as the effect above, and not redundant with it: the effect clears `holderId` on
   * the NEXT render, so without this the ineligible holder would paint as selected for one frame — and the
   * "Crear" button reads this, not the effect.
   */
  const selectedHolder = useMemo(() => {
    const onPage = holderPage.find((h) => h.id === holderId);
    if (onPage) return isEligibleHolder(onPage) ? onPage : null;
    return externalHolder && externalHolder.id === holderId ? externalHolder : null;
  }, [holderPage, holderId, externalHolder]);
  const selectedPocket = useMemo(() => pockets.find((p) => p.id === pocketId) ?? null, [pockets, pocketId]);

  // The card is issued for the holder's organization, which is what decides
  // whether the pause applies — not the organization of the operator creating it.
  const { movementsFrozen } = useCardOperationsMaintenance(
    selectedHolder?.organization_id ?? user?.organization_id,
  );

  const submitDisabled = !holderId || submitting || movementsFrozen;
  const checkingHolders = holdersLoading && holderPage.length === 0;
  /**
   * "This organization has no holders" is only true when nothing was asked for. A search that finds nobody is
   * a search result, not an empty org — claiming otherwise pushed the operator into creating a duplicate
   * titular.
   *
   * This now means what it says. While the request pinned `kyc_group=approved`, an org full of unverified
   * holders answered with an empty page and got the "create your first titular" screen — advice that was
   * actively wrong, since the titular already existed.
   */
  const noHolders = holdersChecked && !holdersLoading && holderPage.length === 0 && !holderQuery.trim();
  /**
   * Holders exist, none of them can be issued a card. The list alone would be a wall of greyed rows with no
   * way forward, so the reason is stated in words — with the badges supplying the per-row detail.
   */
  const noEligibleHolders =
    holdersChecked && !holdersLoading && holderPage.length > 0 && !holderPage.some(isEligibleHolder);

  const goToHolders = () => {
    onOpenChange(false);
    navigate("/app/cards/identities");
  };

  /**
   * The drawer already read the created identity back, so this decides only what to do with the answer.
   *
   * `eligible` is the exception, not the rule — it means the backend's global dedupe reused a person who was
   * already verified. A genuinely new holder is `PENDING_REVIEW` with KYC `PENDING`, so it is announced
   * rather than selected: selecting it would arm a submit the backend rejects.
   *
   * The announcement still earns its place now that the list shows the holder anyway. The list says WHAT is
   * blocking; this says it about the person just created, and carries the only link to the verification they
   * have to complete.
   *
   * Nothing is pushed into the loaded list, not even on the eligible path. `externalHolder` renders the
   * selection on its own, and the list belongs to the server: a locally prepended row desynchronises the
   * `skip` that "load more" is computed from, and can surface a holder that contradicts the search term still
   * showing in the box.
   */
  const handleHolderResolved = useCallback((outcome: NewHolderOutcome) => {
    if (outcome.kind === "eligible") {
      // Seeded before the id, so the by-id resolver above short-circuits instead of issuing a second GET.
      setExternalHolder(outcome.identity);
      setHolderId(outcome.identity.id);
      setPendingHolder(null);
      return;
    }
    if (outcome.kind === "unknown") {
      setPendingHolder({ id: null, name: null });
      return;
    }
    setPendingHolder({
      id: outcome.identityId,
      name: outcome.identity ? resolveIdentityDisplayName(outcome.identity) : null,
    });
  }, []);

  const handleViewHolder = useCallback(
    (identityId: string) => {
      onOpenChange(false);
      navigate(`/app/cards/identities/${identityId}`);
    },
    [navigate, onOpenChange],
  );

  const newHolderAction = canCreateHolder ? (
    <ColocaButton variant="ghost" size="sm" icon={Plus} onClick={() => setIsNewHolderOpen(true)}>
      {t("create_card_new_holder_button")}
    </ColocaButton>
  ) : null;

  /**
   * Rendered in BOTH branches below. After creating a holder who cannot be issued a card, `noHolders` is
   * still true — the server returns the same empty page — so without this the empty state would repaint as if
   * nothing had happened and the operator would create the same person again.
   */
  const pendingHolderId = pendingHolder?.id ?? null;
  const pendingHolderNotice = pendingHolder ? (
    <ColocaAlert
      icon={ShieldAlert}
      variant="warning"
      role="status"
      className="mb-4"
      heading={
        pendingHolder.name
          ? t("create_card_holder_pending_notice_title", { name: pendingHolder.name })
          : t("create_card_holder_pending_notice_title_unnamed")
      }
      // Without an id there is nothing to verify and nowhere to send them, so the copy points at the
      // holders list instead of describing a verification that may or may not be what happened.
      text={
        pendingHolderId
          ? t("create_card_holder_pending_notice_description")
          : t("create_card_holder_created_unknown")
      }
      actions={
        pendingHolderId ? (
          <ColocaButton variant="default" size="sm" onClick={() => handleViewHolder(pendingHolderId)}>
            {t("create_card_holder_created_view")}
          </ColocaButton>
        ) : undefined
      }
    />
  ) : null;

  const performCreate = async (twoFactorCode?: string) => {
    if (!holderId) return;
    setSubmitting(true);
    try {
      const response = await CardsService.createCard(
        {
          identityId: holderId,
          alias,
          pocketId: pocketId ?? undefined,
        },
        twoFactorCode ? { twoFactorCode } : undefined,
      );
      colocaToast.success(t("create_card_success"));
      await onRefresh?.();
      onCreated?.(response.data?.id ?? "");
      onOpenChange(false);
    } catch (err: unknown) {
      if (isTwoFactorCodeError(err)) {
        // The one failure the challenge owns. Throwing keeps it open showing the wrong-code message, which is
        // exactly the contract `TwoFactorChallengeModal` documents.
        throw stepUpRefusal(err, refusalMessage(err, t("two_factor_challenge_error_invalid_code")));
      }
      colocaToast.error(HttpApiService.getErrorMessage(err) || t("identities_error_create_card"));
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (!holderId) return;
    if (hasStepUpMethod(user)) {
      setIsTwoFactorChallengeOpen(true);
      return;
    }
    void performCreate();
  };

  const handleSubmitChallenge = async (code: string) => {
    await performCreate(code);
  };

  const holderDisplayName = selectedHolder ? resolveIdentityDisplayName(selectedHolder) : "—";

  return (
    <ColocaDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("create_card_modal_title")}
      description={t("create_card_modal_description")}
      size="lg"
      footer={
        noHolders ? (
          <div className="flex w-full items-center justify-end gap-2">
            <ColocaButton variant="ghost" onClick={() => onOpenChange(false)}>
              {t("create_identity_modal_button_cancel")}
            </ColocaButton>
            {/* Going to the holders list stays available, but demoted: creating one right here keeps the
                alias, the pocket and any `presetPocketId`/`lockPocket` the caller set — the trip loses them. */}
            <ColocaButton
              variant={canCreateHolder ? "default" : "primary"}
              icon={Users}
              onClick={goToHolders}
            >
              {t("create_card_no_holder_button")}
            </ColocaButton>
            {canCreateHolder && (
              <ColocaButton variant="primary" icon={Plus} onClick={() => setIsNewHolderOpen(true)}>
                {t("create_card_new_holder_button")}
              </ColocaButton>
            )}
          </div>
        ) : (
          <div className="flex w-full items-center justify-end gap-2">
            <ColocaButton variant="ghost" onClick={() => onOpenChange(false)}>
              {t("create_identity_modal_button_cancel")}
            </ColocaButton>
            <ColocaButton
              variant="primary"
              onClick={handleSubmit}
              loading={submitting}
              disabled={submitDisabled}
            >
              {t("create_card_modal_button_create")}
            </ColocaButton>
          </div>
        )
      }
    >
      {movementsFrozen && (
        <ColocaAlert
          icon={Wrench}
          variant="warning"
          className="mb-4"
          heading={t("card_maintenance_banner_title")}
          text={t("card_maintenance_banner_description")}
        />
      )}
      {checkingHolders ? (
        <div className="space-y-6">
          <ColocaSkeleton className="h-40 w-full rounded-xl" />
          <div className="space-y-3">
            <ColocaSkeleton className="h-10 w-full" />
            <ColocaSkeleton className="h-10 w-full" />
            <ColocaSkeleton className="h-10 w-2/3" />
          </div>
        </div>
      ) : noHolders ? (
        <>
          {pendingHolderNotice}
          <div className="flex flex-col items-center gap-4 px-2 py-8 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <Users className="h-6 w-6" aria-hidden="true" />
            </span>
            <div className="space-y-2">
              <h3 className="text-base font-semibold text-foreground">{t("create_card_no_holder_title")}</h3>
              <p className="mx-auto max-w-md text-sm text-muted-foreground">
                {t("create_card_no_holder_description")}
              </p>
            </div>
          </div>
        </>
      ) : (
        <div className="space-y-6">
          {pendingHolderNotice}
          <CardPreviewPanel
            alias={alias}
            holderDisplayName={holderDisplayName}
            selectedPocket={selectedPocket}
          />

          <div className="space-y-5">
            <CardTypeSelector />

            <section className="space-y-2">
              <div className="flex items-baseline justify-between gap-2">
                <label htmlFor="card-alias" className="text-sm font-medium text-foreground">
                  {t("create_card_label_alias")}
                </label>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {alias.length}/{CARD_ALIAS_MAX_LENGTH}
                </span>
              </div>
              <ColocaInput
                id="card-alias"
                type="text"
                value={alias}
                onValueChange={setAlias}
                placeholder={t("create_card_alias_placeholder")}
                maxLength={CARD_ALIAS_MAX_LENGTH}
              />
            </section>

            {noEligibleHolders && (
              <ColocaAlert
                icon={ShieldAlert}
                variant="warning"
                role="status"
                heading={t("create_card_holders_none_eligible_title")}
                text={t("create_card_holders_none_eligible_description")}
              />
            )}

            <HolderPickerField
              holders={holderPage}
              isLoading={holdersLoading}
              selectedHolder={selectedHolder}
              onSelect={setHolderId}
              query={holderQuery}
              onQueryChange={setHolderQuery}
              isSearchTermTooShort={isHolderSearchTooShort}
              hasMore={hasMoreHolders}
              isLoadingMore={isLoadingMoreHolders}
              onLoadMore={loadMoreHolders}
              action={newHolderAction}
            />

            {!lockPocket && pocketsVisible && (
              <PocketPickerField
                pockets={pockets}
                isLoading={pocketsLoading}
                pocketId={pocketId}
                onSelect={setPocketId}
                query={pocketQuery}
                onQueryChange={setPocketQuery}
              />
            )}
          </div>
        </div>
      )}

      {/*
        Rendered INSIDE the dialog's children, never as a sibling. Base UI detects nesting through React
        context, and that detection is what suppresses the child's own backdrop and — the reason this
        matters — scopes Esc to the topmost dialog. As siblings both roots believe they are topmost and
        both listen for `keydown` on `document`, so one Esc closed the challenge AND the card modal
        underneath it, discarding the draft.
      */}
      <TwoFactorChallengeModal
        open={isTwoFactorChallengeOpen}
        onOpenChange={setIsTwoFactorChallengeOpen}
        onSubmit={handleSubmitChallenge}
      />

      {/*
        Same rule as the challenge above: nested, never a sibling. It is also why this is a side panel rather
        than a second dialog — at `lg` it takes the right band and leaves the card draft visible behind it,
        which is the whole reason for creating the holder here instead of navigating away.

        Rendered unconditionally: Base UI's portal returns null while closed, so the form never mounts and
        costs nothing. Gating it on `isNewHolderOpen` would also kill the closing animation.
      */}
      {canCreateHolder && (
        <NewHolderDrawer
          open={isNewHolderOpen}
          onOpenChange={setIsNewHolderOpen}
          onResolved={handleHolderResolved}
          onViewHolder={handleViewHolder}
        />
      )}
    </ColocaDialog>
  );
};
